import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/base/Breadcrumb";
import DramaCard from "@/components/base/DramaCard";
import ActorCard from "@/components/base/ActorCard";
import { allActors, allDramas, searchActors, searchDramas } from "@/utils/content";

type TabKey = "all" | "drama" | "actor";

const TABS: { key: TabKey; label: string }[] = [
  { key: "all", label: "全部" },
  { key: "drama", label: "影视剧" },
  { key: "actor", label: "演员" },
];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("q") ?? "";
  const [input, setInput] = useState(keyword);
  const [tab, setTab] = useState<TabKey>("all");

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  useEffect(() => {
    document.title = `搜索“${keyword || "影视内容"}”结果 - 隔壁的女孩`;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute("content", `搜索“${keyword}”的影视剧与演员结果，快速找到你关心的剧情资讯与演员资料。`);
    }
  }, [keyword]);

  const dramaResults = useMemo(() => searchDramas(keyword), [keyword]);
  const actorResults = useMemo(() => searchActors(keyword), [keyword]);
  const totalCount = dramaResults.length + actorResults.length;

  const hotDramas = useMemo(() => [...allDramas].sort((a, b) => b.popularity - a.popularity).slice(0, 4), []);
  const hotActors = useMemo(() => allActors.slice(0, 4), []);

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const q = input.trim();
    setSearchParams(q ? { q } : {}, { replace: true });
    setTab("all");
  };

  const showDrama = tab === "all" || tab === "drama";
  const showActor = tab === "all" || tab === "actor";
  const hasKeyword = keyword.trim().length > 0;

  return (
    <div className="min-h-screen bg-background-100">
      <SiteHeader />
      <main className="pt-16 md:pt-[72px]">
        <div className="border-b border-background-200 bg-background-50">
          <div className="mx-auto max-w-7xl px-4 md:px-6 py-8 md:py-10">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索" }]} />
            <h1 className="mt-4 font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              {hasKeyword ? (
                <>
                  搜索“<span className="text-accent-600">{keyword}</span>”的结果
                </>
              ) : (
                "搜索影视剧与演员"
              )}
            </h1>

            <form onSubmit={submit} className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="flex flex-1 items-center gap-2 h-12 px-4 rounded-md border border-background-300 bg-background-50 focus-within:border-accent-400">
                <i className="ri-search-line text-foreground-400"></i>
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="输入影视剧名称、题材或演员姓名"
                  className="flex-1 bg-transparent text-sm outline-none text-foreground-900 placeholder:text-foreground-400"
                />
              </div>
              <button
                type="submit"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-accent-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-accent-600 transition-colors cursor-pointer"
              >
                <i className="ri-search-line"></i>
                搜索
              </button>
            </form>
            <p className="mt-3 text-xs text-foreground-500">
              <i className="ri-information-line mr-1"></i>
              当前为前端模拟搜索演示，正式的后端数据接入正在筹备中。
            </p>
          </div>
        </div>

        <div className="mx-auto max-w-7xl px-4 md:px-6 py-8 md:py-10">
          {hasKeyword ? (
            <>
              <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                <p className="text-sm text-foreground-500">
                  共找到 <span className="font-medium text-accent-600">{totalCount}</span> 条结果（影视剧{" "}
                  {dramaResults.length} · 演员 {actorResults.length}）
                </p>
                <div className="flex items-center gap-1 rounded-full bg-background-200 p-1">
                  {TABS.map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => setTab(item.key)}
                      className={`h-8 px-3.5 rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer ${
                        tab === item.key
                          ? "bg-background-50 text-accent-600"
                          : "text-foreground-600 hover:text-foreground-900"
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>

              {totalCount === 0 ? (
                <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16 px-4 text-center">
                  <span className="flex items-center justify-center w-14 h-14 rounded-full bg-background-200 text-foreground-400 mb-4">
                    <i className="ri-search-eye-line text-3xl"></i>
                  </span>
                  <h3 className="font-heading text-lg font-semibold text-foreground-900">
                    没有找到与“{keyword}”相关的内容
                  </h3>
                  <p className="mt-2 max-w-md text-sm text-foreground-500">
                    换个关键词试试，或者看看下面这些热门影视剧，说不定就有你想追的那一部。
                  </p>
                  <div className="mt-6 grid w-full max-w-3xl grid-cols-2 gap-4 sm:grid-cols-4">
                    {hotDramas.map((drama) => (
                      <DramaCard key={drama.id} drama={drama} />
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-10">
                  {showDrama && dramaResults.length > 0 && (
                    <section>
                      <h2 className="font-heading text-lg font-semibold text-foreground-950 mb-4 flex items-center gap-2">
                        <i className="ri-film-line text-accent-600"></i>
                        影视剧结果
                      </h2>
                      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 md:gap-5">
                        {dramaResults.map((drama) => (
                          <DramaCard key={drama.id} drama={drama} />
                        ))}
                      </div>
                    </section>
                  )}

                  {showActor && actorResults.length > 0 && (
                    <section>
                      <h2 className="font-heading text-lg font-semibold text-foreground-950 mb-4 flex items-center gap-2">
                        <i className="ri-user-star-line text-accent-600"></i>
                        演员结果
                      </h2>
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {actorResults.map((actor) => (
                          <ActorCard key={actor.id} actor={actor} />
                        ))}
                      </div>
                    </section>
                  )}

                  {((showDrama && dramaResults.length === 0 && tab === "drama") ||
                    (showActor && actorResults.length === 0 && tab === "actor")) && (
                    <p className="rounded-lg border border-dashed border-background-300 bg-background-50 py-10 text-center text-sm text-foreground-500">
                      该分类下暂无匹配结果，试试切换到“全部”标签。
                    </p>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="space-y-10">
              <section>
                <div className="flex items-end justify-between mb-5">
                  <h2 className="font-heading text-lg font-semibold text-foreground-950 flex items-center gap-2">
                    <i className="ri-fire-line text-accent-600"></i>
                    热门影视剧
                  </h2>
                  <Link to="/category" className="text-sm font-medium text-accent-600 hover:text-accent-700">
                    查看全部
                  </Link>
                </div>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 md:gap-5">
                  {hotDramas.map((drama) => (
                    <DramaCard key={drama.id} drama={drama} />
                  ))}
                </div>
              </section>
              <section>
                <h2 className="font-heading text-lg font-semibold text-foreground-950 mb-5 flex items-center gap-2">
                  <i className="ri-user-star-line text-accent-600"></i>
                  热门演员
                </h2>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {hotActors.map((actor) => (
                    <ActorCard key={actor.id} actor={actor} />
                  ))}
                </div>
              </section>
            </div>
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}